import { ENV } from '../../config/env';

// ── Types ──

export interface JobSummary {
    id: string;
    title: string;
    companyId?: string;
    companyName: string;
    location: string;
    employmentType: string;
    experienceLevel: string;
}

export interface CandidateMatchResult {
    candidate: {
        id: string;
        supabaseUserId: string;
        email: string;
        name: string;
        skills?: string[];
        yearsExp?: number;
        degreesList?: string[];
        allJobTitles?: string[];
        allCompanies?: string[];
        locations?: string[];
        linkedinUrl?: string;
        importSource?: string;
        isActive: boolean;
    };
    matchScore: number;
    vectorSimilarity: number;
    qwenScore: number;
    softCriteriaScore: number;
    source: 'platform' | 'imported' | 'imported_se';
}

export interface FindCandidatesResponse {
    jobId: string;
    matches: CandidateMatchResult[];
    totalCandidates: number;
    processingTimeMs: number;
}

export interface ApplicantInfo {
    candidateId: string; // Supabase UUID
    candidateName: string;
    candidateEmail: string;
    stage: string;
    appliedAt: string;
}

// ── Auth helpers ──

const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
    };
};

// ── API Functions ──

/**
 * List distinct company names from active jobs
 */
export const listCompanies = async (search?: string): Promise<string[]> => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);

    const url = `${ENV.JOB_SERVICE_URL}/admin/v1/companies${params.toString() ? '?' + params : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });

    if (!response.ok) {
        throw new Error(`Failed to list companies: ${response.status}`);
    }

    const data = await response.json();
    return data.payload?.companies || data.companies || [];
};

/**
 * List jobs for a company, with optional title search
 */
export const listJobs = async (companyName: string, search?: string): Promise<JobSummary[]> => {
    const params = new URLSearchParams({ company_name: companyName });
    if (search) params.set('search', search);

    const url = `${ENV.JOB_SERVICE_URL}/admin/v1/jobs?${params}`;
    const response = await fetch(url, { headers: getAuthHeaders() });

    if (!response.ok) {
        throw new Error(`Failed to list jobs: ${response.status}`);
    }

    const data = await response.json();
    return data.payload?.jobs || data.jobs || [];
};

/**
 * Find matching candidates for a job (platform + imported)
 */
export const findCandidatesForJob = async (jobId: string): Promise<FindCandidatesResponse> => {
    const url = `${ENV.MATCHES_SERVICE_URL}/admin/v1/jobs/${jobId}/find-candidates`;
    const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.details || `Failed to find candidates: ${response.status}`);
    }

    return response.json();
};

/**
 * Get applicants for a job (candidate IDs who have applied)
 */
export const getJobApplicants = async (jobId: string): Promise<ApplicantInfo[]> => {
    const url = `${ENV.JOB_SERVICE_URL}/admin/v1/jobs/${jobId}/applicants`;
    const response = await fetch(url, { headers: getAuthHeaders() });

    if (!response.ok) {
        throw new Error(`Failed to get applicants: ${response.status}`);
    }

    const data = await response.json();
    return data.payload?.applicants || data.applicants || [];
};
